import { translationService } from '../services/translation'

/**
 * Composable that smart-translates book descriptions.
 *
 * The backend detects the actual language of each description and only
 * translates when the detected language differs from the user's current
 * language.  This handles two important cases:
 *
 *  1. A description stored in French is shown to an English-speaking user
 *     → translated to English before rendering.
 *  2. A description stored in French is viewed by a user who has selected
 *     French → returned unchanged (no unnecessary round-trip).
 */
export function useBookDescriptionTranslation () {
  /**
   * Translate descriptions for an array of books.
   *
   * @param {Object[]} books        – Array of book objects (must have `description` field)
   * @param {string|null} targetLang – BCP-47 language code; defaults to current UI language
   * @returns {Promise<Object[]>}   – New array where `description` fields are translated
   */
  const translateDescriptions = async (books, targetLang = null) => {
    if (!books || books.length === 0) return books

    const descriptions = books.map(b => b.description || '')

    // Non-empty descriptions that need processing
    const nonEmpty = descriptions.filter(d => d && d.trim())
    if (nonEmpty.length === 0) return books

    const results = await translationService.smartTranslateTexts(descriptions, targetLang)

    return books.map((book, i) => {
      const result = results[i]
      if (!result) return book
      return {
        ...book,
        description: result.translatedText || book.description
      }
    })
  }

  /**
   * Translate the description of a single book object.
   *
   * @param {Object} book
   * @param {string|null} targetLang
   * @returns {Promise<Object>}
   */
  const translateBookDescription = async (book, targetLang = null) => {
    if (!book) return book
    const [translated] = await translateDescriptions([book], targetLang)
    return translated
  }

  return {
    translateDescriptions,
    translateBookDescription
  }
}
